import { Button } from 'components/ui/button';
import { checkSpanishLocale, cn, extractLabelAndComment } from 'lib/utils';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { PATH_ROUTES } from 'routes/path';
import { getSymptomQuestion } from 'services/symptom-checker/api';
import { useSymptomStore } from 'store/symptom';
import { useTranslation } from 'react-i18next';

const ENGLISH_YES = 'Yes';
const SPANISH_YES = 'Si';

const checkIsYes = (value: string) => {
  return [ENGLISH_YES.toLowerCase(), SPANISH_YES.toLowerCase(), 'sí'].includes(
    value.toLowerCase()
  );
};

export default function SymptomCheckerConfirmation() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const questionData = useSymptomStore((store) => store.questionData)?.data;
  const setQuestionData = useSymptomStore((store) => store.setQuestionData);

  const isSpanish = checkSpanishLocale();

  const englishChoices = JSON.parse(questionData?.eng_choices || '');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '');

  const buttons = isSpanish ? spanishChoices : englishChoices;

  const [isLoading, setIsLoading] = useState<string>('');

  const onSelect = async (choice: string) => {
    try {
      setIsLoading(choice);
      const response = await getSymptomQuestion({
        type: 'latest',
        restart_flag: checkIsYes(choice) ? 'true' : 'false',
      });
      console.log('🚀 ~ onSelect ~ response:', response);

      if (response.screen === 'review') {
        setQuestionData(response);
        return navigate(PATH_ROUTES.GENERATE_SYMPTOM_REVIEW);
      }

      if (response?.data?.q_id) {
        setQuestionData(response);
      }
    } catch (error) {
      toast.error(t('failed_to_get_question'));
    } finally {
      setIsLoading('');
    }
  };

  const { label } = extractLabelAndComment(
    questionData?.[isSpanish ? 'spanish_question' : 'eng_question'] ?? ''
  );

  return (
    <div className='flex flex-col items-center justify-center min-h-[calc(100vh-10rem)] space-y-8 max-w-2xl mx-auto px-6'>
      <p className='text-lg font-semibold text-center'>{label}</p>

      <div className='flex flex-col items-center justify-center w-full gap-4'>
        {buttons.map((btn: string) => (
          <Button
            key={btn}
            onClick={() => onSelect(btn)}
            disabled={!!isLoading}
            className={cn(
              'w-full md:w-1/2',
              checkIsYes(btn) && 'border-gray-500'
            )}
            variant={checkIsYes(btn) ? 'outline' : 'default'}
          >
            {isLoading === btn && (
              <Loader2 className='mr-2 animate-spin size-4' />
            )}
            {btn}
          </Button>
        ))}
      </div>
    </div>
  );
}
